import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import {
  FocusContext,
  setFocus,
  useFocusable,
} from '@noriginmedia/norigin-spatial-navigation'

import { useAppDispatch, useAppSelector } from '../../../hooks/useStore'
import {
  useKeyDown,
  KeyTypeEnum,
} from '../../../../../shared/providers/KeyDownProvider'
import { platformTransitionStyle } from '../../../../../shared/helpers'
import { CaptionsButton } from '../../CaptionsButton/CaptionsButton'
import tts from '../../../lib/TTS'
import { useVideoPlayerData } from '../VideoPlayerContext'

import ProgressBar from './ProgressBar/ProgressBar'

import type { PlayerControlsProps } from './types'
import ForbiddenActionMessage from '../../ForbiddenActionMessage'
import dayjs from 'dayjs'

declare const COMPILING_FOR_TIZEN: boolean

function PlayerControls(props: PlayerControlsProps) {
  const {
    playerStateRef,
    setLastUserInteraction,
    setIsUserTryingToSeek,
    setPlayerStatus,
    setIsUpNextVisible,
  } = useVideoPlayerData()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const location = useLocation()
  const [showControls, setShowControls] = useState(false)
  const areCaptionsEnabled = useAppSelector((state) => state.captions.enabled)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const adInteraction = ['adPlaying', 'adPaused'].includes(
    playerStateRef.current.status
  )
  const keysToListen = [
    KeyTypeEnum.Right,
    KeyTypeEnum.Left,
    KeyTypeEnum.FastForward,
    KeyTypeEnum.Rewind,
  ]

  const { focusKey } = useFocusable({
    preferredChildFocusKey: 'progress-bar',
  })

  const toggleCaptions = useCallback(() => {
    dispatch({
      type: 'SET_CAPTION_STATE',
      payload: { enabled: !areCaptionsEnabled },
    })
    tts.addTextToRead(`Captions ${areCaptionsEnabled ? 'Off' : 'On'}`)
  }, [areCaptionsEnabled])

  const goBackToVideoDetails = useCallback(() => {
    const windowUrl = window.location.href.split('/')[4]
    navigate(`/video-details/${windowUrl}`, {
      replace: true,
      state: {
        ...location.state,
        fromVOD: true,
      },
    })
  }, [navigate])

  useKeyDown(
    [
      KeyTypeEnum.Right,
      KeyTypeEnum.Left,
      KeyTypeEnum.Up,
      KeyTypeEnum.Down,
      KeyTypeEnum.Enter,
      KeyTypeEnum.Back,
      KeyTypeEnum.FastForward,
      KeyTypeEnum.Rewind,
      KeyTypeEnum.Stop,
      KeyTypeEnum.Pause,
      KeyTypeEnum.Play,
    ],
    (key) => {
      setLastUserInteraction(dayjs())
      if (!playerStateRef.current.upNextData?.isUpNextVisible) {
        if (key === KeyTypeEnum.Back) {
          if (showControls) {
            if (playerStateRef.current.isUserTryingToSeek) {
              setIsUserTryingToSeek(false)
              props.play()
              setPlayerStatus('playing')
            } else {
              setShowControls(false)
            }
          } else {
            goBackToVideoDetails()
          }
        } else if (key === KeyTypeEnum.Stop) {
          goBackToVideoDetails()
        } else if (!showControls) {
          setShowControls(true)
          setFocus('progress-bar')
          resetTimer()
        } else {
          resetTimer()
        }
      } else {
        if (key === KeyTypeEnum.Back) {
          setIsUpNextVisible(false)
        }
      }
    }
  )

  useEffect(() => {
    if (playerStateRef.current.upNextData?.isUpNextVisible) {
      setShowControls(false)
    }
  }, [playerStateRef.current.upNextData?.isUpNextVisible])

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    }
  }, [])

  const resetTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
    }
    timerRef.current = setTimeout(() => {
      if (!playerStateRef.current.isUserTryingToSeek) {
        setShowControls(false)
      } else {
        resetTimer()
      }
    }, 3000)
  }, [playerStateRef.current.status])

  return (
    <FocusContext.Provider value={focusKey}>
      {adInteraction && <ForbiddenActionMessage keysToListen={keysToListen} />}
      {props.captionsAvailable && !COMPILING_FOR_TIZEN && showControls && (
        <CaptionsButton active={areCaptionsEnabled} onEnter={toggleCaptions} />
      )}
      <div
        id="video-player-underlay"
        style={{
          transition: platformTransitionStyle('opacity 500ms linear'),
          opacity: showControls ? 1 : 0,
        }}
      >
        <div id="video-player-ui">
          <ProgressBar
            playOrPause={props.playOrPause}
            seek={props.seek}
            pause={props.pause}
            play={props.play}
          />
        </div>
      </div>
    </FocusContext.Provider>
  )
}

export default PlayerControls
